/**
 * Bill Form Page - Create/Edit Vendor AP Bill
 * Leviathan - Fuel Jobber Back Office
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { API_URL } from '@/config/api';

interface Supplier {
  id: string;
  supplierNumber: string;
  name: string;
  paymentTerms: number | null;
}

interface Receipt {
  id: string;
  receiptNumber: string;
  supplierInvoice: string | null;
  total: number;
  status: string;
  items: Array<{
    productId: string;
    product: { name: string };
    receivedQuantity: number;
    unit: string;
    unitCost: number;
    lineTotal: number;
  }>;
}

interface BillItem {
  productId: string | null;
  description: string;
  quantity: number;
  unit: string;
  unitCost: number;
  lineTotal: number;
}

interface FormData {
  supplierId: string;
  receiptId: string;
  vendorInvoiceNumber: string;
  vendorInvoiceDate: string;
  billDate: string;
  paymentTerms: string;
  taxAmount: number;
  shippingAmount: number;
  otherCharges: number;
  notes: string;
  internalNotes: string;
  items: BillItem[];
}

const PAYMENT_TERMS = [
  { value: 'DUE_ON_RECEIPT', label: 'Due on Receipt' },
  { value: 'NET10', label: 'Net 10' },
  { value: 'NET15', label: 'Net 15' },
  { value: 'NET30', label: 'Net 30' },
  { value: 'NET45', label: 'Net 45' },
  { value: 'NET60', label: 'Net 60' },
];

export default function BillFormPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const isEditMode = Boolean(id);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [receipts, setReceipts] = useState<Receipt[]>([]);

  const [formData, setFormData] = useState<FormData>({
    supplierId: searchParams.get('supplierId') || '',
    receiptId: searchParams.get('receiptId') || '',
    vendorInvoiceNumber: '',
    vendorInvoiceDate: new Date().toISOString().split('T')[0] || '',
    billDate: new Date().toISOString().split('T')[0] || '',
    paymentTerms: 'NET30',
    taxAmount: 0,
    shippingAmount: 0,
    otherCharges: 0,
    notes: '',
    internalNotes: '',
    items: [],
  });

  useEffect(() => {
    fetchSuppliers();
    if (isEditMode) {
      fetchBill();
    }
  }, [id]);

  useEffect(() => {
    if (formData.supplierId) {
      fetchReceiptsForSupplier(formData.supplierId);
      const supplier = suppliers.find((s) => s.id === formData.supplierId);
      if (supplier && !isEditMode) {
        setFormData((prev) => ({
          ...prev,
          paymentTerms: supplier.paymentTerms ? `NET${supplier.paymentTerms}` : 'NET30',
        }));
      }
    }
  }, [formData.supplierId, suppliers]);

  useEffect(() => {
    // If receipt is selected, populate items from receipt
    if (formData.receiptId && receipts.length > 0) {
      const receipt = receipts.find((r) => r.id === formData.receiptId);
      if (receipt && formData.items.length === 0) {
        setFormData((prev) => ({
          ...prev,
          vendorInvoiceNumber: receipt.supplierInvoice || '',
          items: receipt.items.map((item) => ({
            productId: item.productId,
            description: item.product?.name || '',
            quantity: item.receivedQuantity,
            unit: item.unit,
            unitCost: item.unitCost,
            lineTotal: item.lineTotal,
          })),
        }));
      }
    }
  }, [formData.receiptId, receipts]);

  const fetchSuppliers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/suppliers?isActive=true`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setSuppliers(data.suppliers || []);
      }
    } catch (err) {
      console.error('Failed to fetch suppliers:', err);
    }
  };

  const fetchReceiptsForSupplier = async (supplierId: string) => {
    try {
      const response = await fetch(
        `${API_URL}/api/receipts?supplierId=${supplierId}&status=POSTED`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setReceipts(data.receipts || []);
      }
    } catch (err) {
      console.error('Failed to fetch receipts:', err);
    }
  };

  const fetchBill = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/bills/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        const bill = data.bill;
        setFormData({
          supplierId: bill.supplierId,
          receiptId: bill.receiptId || '',
          vendorInvoiceNumber: bill.vendorInvoiceNumber || '',
          vendorInvoiceDate: bill.vendorInvoiceDate?.split('T')[0] || '',
          billDate: bill.billDate?.split('T')[0] || '',
          paymentTerms: bill.paymentTerms || 'NET30',
          taxAmount: bill.taxAmount || 0,
          shippingAmount: bill.shippingAmount || 0,
          otherCharges: bill.otherCharges || 0,
          notes: bill.notes || '',
          internalNotes: bill.internalNotes || '',
          items: bill.items.map((item: BillItem) => ({
            productId: item.productId,
            description: item.description,
            quantity: item.quantity,
            unit: item.unit,
            unitCost: item.unitCost,
            lineTotal: item.lineTotal,
          })),
        });
      } else {
        setError('Bill not found');
      }
    } catch (err) {
      setError('Failed to load bill');
    } finally {
      setIsLoading(false);
    }
  };

  const addItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          productId: null,
          description: '',
          quantity: 1,
          unit: 'gallon',
          unitCost: 0,
          lineTotal: 0,
        },
      ],
    }));
  };

  const updateItem = (index: number, field: string, value: string | number) => {
    setFormData((prev) => {
      const newItems = [...prev.items];
      const currentItem = newItems[index];
      if (!currentItem) return prev;

      const item = { ...currentItem, [field]: value };
      item.lineTotal = item.quantity * item.unitCost;
      newItems[index] = item;
      return { ...prev, items: newItems };
    });
  };

  const removeItem = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + item.lineTotal, 0);
    const total = subtotal + formData.taxAmount + formData.shippingAmount + formData.otherCharges;
    return { subtotal, total };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.supplierId) {
      setError('Please select a supplier');
      return;
    }

    if (formData.items.length === 0) {
      setError('Please add at least one line item');
      return;
    }

    setIsSaving(true);

    try {
      const url = isEditMode ? `${API_URL}/api/bills/${id}` : `${API_URL}/api/bills`;

      const response = await fetch(url, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          ...formData,
          receiptId: formData.receiptId || null,
        }),
      });

      if (response.ok) {
        navigate('/ap');
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to save bill');
      }
    } catch (err) {
      setError('Failed to save bill');
    } finally {
      setIsSaving(false);
    }
  };

  const { subtotal, total } = calculateTotals();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-text-tertiary font-mono animate-pulse">&gt; Loading bill...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber" />
        <div className="border-l-4 border-amber pl-6">
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
              >
                {isEditMode ? 'Edit Bill' : 'New Bill'}
              </h1>
              <p className="text-text-secondary text-sm font-mono mt-1">
                {isEditMode ? 'Modify vendor bill' : 'Record a vendor bill'}
              </p>
            </div>
            <button
              onClick={() => navigate('/ap')}
              className="text-text-secondary hover:text-amber font-mono text-sm uppercase tracking-wider"
            >
              ← Back to Bills
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-error-red/10 border border-error-red/50 text-error-red p-4 font-mono text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Supplier & Receipt */}
        <Panel title="Bill Information">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Supplier *
              </label>
              <select
                value={formData.supplierId}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    supplierId: e.target.value,
                    receiptId: '',
                    items: [],
                  }))
                }
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
                required
                disabled={isEditMode}
              >
                <option value="">Select Supplier...</option>
                {suppliers.map((supplier) => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.name} ({supplier.supplierNumber})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                From Receipt (Optional)
              </label>
              <select
                value={formData.receiptId}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, receiptId: e.target.value, items: [] }))
                }
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
                disabled={!formData.supplierId || isEditMode}
              >
                <option value="">No linked receipt</option>
                {receipts.map((receipt) => (
                  <option key={receipt.id} value={receipt.id}>
                    {receipt.receiptNumber} - ${receipt.total.toFixed(2)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Vendor Invoice #
              </label>
              <input
                type="text"
                value={formData.vendorInvoiceNumber}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, vendorInvoiceNumber: e.target.value }))
                }
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Vendor Invoice Date
              </label>
              <input
                type="date"
                value={formData.vendorInvoiceDate}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, vendorInvoiceDate: e.target.value }))
                }
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Bill Date
              </label>
              <input
                type="date"
                value={formData.billDate}
                onChange={(e) => setFormData((prev) => ({ ...prev, billDate: e.target.value }))}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Payment Terms
              </label>
              <select
                value={formData.paymentTerms}
                onChange={(e) => setFormData((prev) => ({ ...prev, paymentTerms: e.target.value }))}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              >
                {PAYMENT_TERMS.map((term) => (
                  <option key={term.value} value={term.value}>
                    {term.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </Panel>

        {/* Line Items */}
        <Panel title="Line Items">
          <div className="space-y-4">
            {formData.items.length === 0 ? (
              <div className="text-center py-8 text-text-tertiary font-mono">
                No items added yet
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[700px]">
                  <thead className="border-b border-panel-border">
                    <tr>
                      <th className="text-left px-4 py-2 text-text-secondary text-xs uppercase tracking-widest font-mono">
                        Description
                      </th>
                      <th className="text-right px-4 py-2 text-text-secondary text-xs uppercase tracking-widest font-mono w-24">
                        Qty
                      </th>
                      <th className="text-left px-4 py-2 text-text-secondary text-xs uppercase tracking-widest font-mono w-24">
                        Unit
                      </th>
                      <th className="text-right px-4 py-2 text-text-secondary text-xs uppercase tracking-widest font-mono w-28">
                        Cost
                      </th>
                      <th className="text-right px-4 py-2 text-text-secondary text-xs uppercase tracking-widest font-mono w-28">
                        Total
                      </th>
                      <th className="w-12"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.items.map((item, index) => (
                      <tr key={index} className="border-b border-panel-border">
                        <td className="px-4 py-2">
                          <input
                            type="text"
                            value={item.description}
                            onChange={(e) => updateItem(index, 'description', e.target.value)}
                            className="w-full bg-background border border-panel-border focus:border-amber px-2 py-1 text-text-primary font-mono text-sm"
                          />
                        </td>
                        <td className="px-4 py-2">
                          <input
                            type="number"
                            value={item.quantity || ''}
                            onChange={(e) =>
                              updateItem(index, 'quantity', parseFloat(e.target.value) || 0)
                            }
                            min="0"
                            className="w-full bg-background border border-panel-border focus:border-amber px-2 py-1 text-text-primary font-mono text-sm text-right"
                          />
                        </td>
                        <td className="px-4 py-2">
                          <input
                            type="text"
                            value={item.unit}
                            onChange={(e) => updateItem(index, 'unit', e.target.value)}
                            className="w-full bg-background border border-panel-border focus:border-amber px-2 py-1 text-text-primary font-mono text-sm"
                          />
                        </td>
                        <td className="px-4 py-2">
                          <input
                            type="number"
                            value={item.unitCost || ''}
                            onChange={(e) =>
                              updateItem(index, 'unitCost', parseFloat(e.target.value) || 0)
                            }
                            min="0"
                            step="0.001"
                            className="w-full bg-background border border-panel-border focus:border-amber px-2 py-1 text-text-primary font-mono text-sm text-right"
                          />
                        </td>
                        <td className="px-4 py-2 text-right">
                          <span className="text-amber font-mono font-semibold">
                            ${item.lineTotal.toFixed(2)}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-center">
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="text-error-red hover:text-error-red/80 font-mono text-sm"
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <button
              type="button"
              onClick={addItem}
              className="w-full border border-dashed border-panel-border hover:border-amber text-text-secondary hover:text-amber py-3 font-mono text-sm transition-colors"
            >
              + Add Line Item
            </button>
          </div>
        </Panel>

        {/* Totals & Notes */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Panel title="Notes">
            <div className="space-y-4">
              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
                />
              </div>
              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  Internal Notes
                </label>
                <textarea
                  value={formData.internalNotes}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, internalNotes: e.target.value }))
                  }
                  rows={2}
                  className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
                />
              </div>
            </div>
          </Panel>

          <Panel title="Bill Totals">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-text-secondary font-mono">Subtotal</span>
                <span className="text-text-primary font-mono">${subtotal.toFixed(2)}</span>
              </div>

              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-2">
                  <span className="text-text-secondary font-mono">Tax</span>
                  <input
                    type="number"
                    value={formData.taxAmount || ''}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        taxAmount: parseFloat(e.target.value) || 0,
                      }))
                    }
                    min="0"
                    step="0.01"
                    className="w-24 bg-background border border-panel-border focus:border-amber px-2 py-1 text-text-primary font-mono text-sm text-right"
                  />
                </div>
                <span className="text-text-primary font-mono">${formData.taxAmount.toFixed(2)}</span>
              </div>

              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-2">
                  <span className="text-text-secondary font-mono">Shipping</span>
                  <input
                    type="number"
                    value={formData.shippingAmount || ''}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        shippingAmount: parseFloat(e.target.value) || 0,
                      }))
                    }
                    min="0"
                    step="0.01"
                    className="w-24 bg-background border border-panel-border focus:border-amber px-2 py-1 text-text-primary font-mono text-sm text-right"
                  />
                </div>
                <span className="text-text-primary font-mono">
                  ${formData.shippingAmount.toFixed(2)}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-2">
                  <span className="text-text-secondary font-mono">Other</span>
                  <input
                    type="number"
                    value={formData.otherCharges || ''}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        otherCharges: parseFloat(e.target.value) || 0,
                      }))
                    }
                    min="0"
                    step="0.01"
                    className="w-24 bg-background border border-panel-border focus:border-amber px-2 py-1 text-text-primary font-mono text-sm text-right"
                  />
                </div>
                <span className="text-text-primary font-mono">
                  ${formData.otherCharges.toFixed(2)}
                </span>
              </div>

              <div className="border-t border-panel-border pt-4 flex justify-between items-center">
                <span className="text-text-primary font-mono font-semibold text-lg">TOTAL</span>
                <span
                  className="text-amber font-mono font-bold text-2xl"
                  style={{ textShadow: '0 0 10px rgba(255, 149, 0, 0.5)' }}
                >
                  ${total.toFixed(2)}
                </span>
              </div>
            </div>
          </Panel>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/ap')}
            className="bg-panel-border hover:bg-steel/20 text-text-primary font-mono py-3 px-8 transition-all uppercase tracking-wider"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="bg-amber hover:bg-amber/90 text-background font-display font-bold py-3 px-8 transition-all uppercase tracking-wider disabled:opacity-50"
            style={{ boxShadow: '0 0 20px rgba(255, 149, 0, 0.4)' }}
          >
            {isSaving ? 'Saving...' : isEditMode ? 'Update Bill' : 'Create Bill'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="relative stat-card">
      <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
      <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
      <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
      <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

      <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
        <h2 className="text-lg font-display text-text-primary uppercase tracking-wide mb-4 pb-3 border-b border-amber">
          {title}
        </h2>
        {children}
      </div>
    </div>
  );
}
